/**
 * Warm Leads page - Referral and warm leads with quick entry + integrated dialer
 *
 * Simpler than InboundPage: no browsing history, UTM, or Meta sections.
 * Quick entry form at top for fast lead capture (<5 seconds).
 */

import { useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Phone,
  X,
  DollarSign,
  ThumbsDown,
  RefreshCw,
  AlertCircle,
  Copy,
  ChevronDown,
  CheckCircle,
  Flame,
  Plus,
  UserPlus,
  FileText,
  User,
  Mail,
  Building2,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CallUI } from '@/components/CallUI';
import { useHasDialer } from '@/lib/auth/AuthProvider';
import { useProspects, useCreateProspect, useUpdateProspect } from '@/lib/api/hooks/useProspects';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { InlineEditField } from '@/components/prospects/InlineEditField';
import type { Prospect, ProspectStatus } from '@/types/prospect';

// View types for filtering warm leads
type WarmView = 'new' | 'follow_up' | 'pipeline' | 'closed' | 'all';

// Status groups for each view
const VIEW_STATUSES: Record<WarmView, ProspectStatus[] | null> = {
  new: ['new', 'qualified'],
  follow_up: ['attempted', 'contacted'],
  pipeline: ['interested', 'proposal'],
  closed: ['closed_won', 'closed_lost', 'disqualified', 'not_interested'],
  all: null,
};

// Status display configuration
const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  scheduled: { label: 'Scheduled', className: 'bg-green-100 text-green-800 border-green-300' },
  qualified: { label: 'Qualified', className: 'bg-blue-100 text-blue-800 border-blue-300' },
  attempted: { label: 'Attempted', className: 'bg-orange-100 text-orange-800 border-orange-300' },
  contacted: { label: 'Contacted', className: 'bg-purple-100 text-purple-800 border-purple-300' },
  interested: { label: 'Interested', className: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
  proposal: { label: 'Proposal', className: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  closed_won: { label: 'Won', className: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  closed_lost: { label: 'Lost', className: 'bg-red-100 text-red-800 border-red-300' },
  disqualified: { label: 'Disqualified', className: 'bg-gray-100 text-gray-600 border-gray-300' },
  not_interested: { label: 'Not Interested', className: 'bg-gray-100 text-gray-600 border-gray-300' },
};

const LOST_REASONS = [
  { value: 'pricing', label: 'Pricing too high' },
  { value: 'timing', label: 'Bad timing' },
  { value: 'competitor', label: 'Went with competitor' },
  { value: 'no_budget', label: 'No budget' },
  { value: 'no_response', label: 'No response' },
  { value: 'not_a_fit', label: 'Not a good fit' },
  { value: 'other', label: 'Other' },
];

function formatRelativeTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] || {
    label: status.replace(/_/g, ' '),
    className: 'bg-gray-100 text-gray-600 border-gray-300'
  };

  return (
    <Badge variant="outline" className={`${config.className} capitalize`}>
      {config.label}
    </Badge>
  );
}

// ===== Modals (same pattern as InboundPage) =====

function CloseWonModal({ open, onClose, onConfirm, prospectName, isLoading }: {
  open: boolean;
  onClose: () => void;
  onConfirm: (amount: number) => void;
  prospectName: string;
  isLoading?: boolean;
}) {
  const [amount, setAmount] = useState('');

  const handleSubmit = () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) return;
    onConfirm(numAmount);
    setAmount('');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Close Won - {prospectName}
          </DialogTitle>
          <DialogDescription>
            Enter the deal amount to mark this lead as closed won.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor="amount">Deal Amount ($)</Label>
          <Input
            id="amount"
            type="number"
            placeholder="e.g., 5000"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-2"
            min="0"
            step="0.01"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!amount || parseFloat(amount) <= 0 || isLoading}
            className="bg-green-600 hover:bg-green-700"
          >
            {isLoading ? 'Saving...' : 'Close Won'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CloseLostModal({ open, onClose, onConfirm, prospectName, isLoading }: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string, notes?: string) => void;
  prospectName: string;
  isLoading?: boolean;
}) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = () => {
    if (!reason) return;
    onConfirm(reason, notes || undefined);
    setReason('');
    setNotes('');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ThumbsDown className="h-5 w-5 text-red-600" />
            Close Lost - {prospectName}
          </DialogTitle>
          <DialogDescription>
            Select a reason why this deal was lost.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <Label htmlFor="reason">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {LOST_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Additional context..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-2"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!reason || isLoading}
            variant="destructive"
          >
            {isLoading ? 'Saving...' : 'Close Lost'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisqualifyModal({ open, onClose, onConfirm, prospectName, isLoading }: {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void;
  prospectName: string;
  isLoading?: boolean;
}) {
  const [notes, setNotes] = useState('');

  const handleSubmit = () => {
    onConfirm(notes);
    setNotes('');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <X className="h-5 w-5 text-gray-600" />
            Disqualify - {prospectName}
          </DialogTitle>
          <DialogDescription>
            Add notes explaining why this lead is not a fit (for reporting).
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor="disqualify-notes">Qualification Notes</Label>
          <Textarea
            id="disqualify-notes"
            placeholder="e.g., Wrong industry, too small, not decision maker..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-2"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading} variant="secondary">
            {isLoading ? 'Saving...' : 'Disqualify'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== Quick Entry Form =====

function QuickEntryForm({ onSuccess }: { onSuccess?: () => void }) {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [referralSource, setReferralSource] = useState('');

  const createProspect = useCreateProspect();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;

    try {
      await createProspect.mutateAsync({
        phone: phone.trim(),
        name: name.trim() || undefined,
        company_name: company.trim() || undefined,
        email: email.trim() || undefined,
        notes: notes.trim() || undefined,
        source: 'warm_referral',
        referral_source: referralSource.trim() || undefined,
        status: 'new',
      });

      toast.success(`Added ${name.trim() || phone.trim()} as warm lead`);

      // Reset form
      setPhone('');
      setName('');
      setCompany('');
      setEmail('');
      setNotes('');
      setReferralSource('');
      setShowMore(false);

      onSuccess?.();
    } catch (err) {
      console.error('Failed to create warm lead:', err);
      toast.error('Failed to add lead. Please try again.');
    }
  };

  return (
    <Card className="border-orange-200 bg-orange-50/30">
      <CardContent className="pt-4 pb-4">
        <form onSubmit={handleSubmit}>
          {/* Compact single-row form */}
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[140px]">
              <Label htmlFor="quick-name" className="text-xs">Name</Label>
              <Input
                id="quick-name"
                placeholder="John Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-9 bg-white"
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label htmlFor="quick-phone" className="text-xs">
                Phone <span className="text-red-500">*</span>
              </Label>
              <Input
                id="quick-phone"
                placeholder="604-555-1234"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="h-9 bg-white"
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label htmlFor="quick-company" className="text-xs">Company</Label>
              <Input
                id="quick-company"
                placeholder="ABC Construction"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="h-9 bg-white"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowMore(!showMore)}
              className="h-9 text-muted-foreground"
            >
              <Plus className={`h-3 w-3 mr-1 transition-transform ${showMore ? 'rotate-45' : ''}`} />
              {showMore ? 'Less' : 'More'}
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!phone.trim() || createProspect.isPending}
              className="h-9 bg-orange-600 hover:bg-orange-700"
            >
              <UserPlus className="h-3 w-3 mr-1" />
              {createProspect.isPending ? 'Adding...' : 'Add Lead'}
            </Button>
          </div>

          {/* Expanded fields */}
          {showMore && (
            <div className="flex items-end gap-2 flex-wrap mt-3 pt-3 border-t border-orange-200/50">
              <div className="flex-1 min-w-[140px]">
                <Label htmlFor="quick-email" className="text-xs">Email</Label>
                <Input
                  id="quick-email"
                  type="email"
                  placeholder="john@abc.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-9 bg-white"
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <Label htmlFor="quick-referral" className="text-xs">Referred by</Label>
                <Input
                  id="quick-referral"
                  placeholder="Jamie Banks"
                  value={referralSource}
                  onChange={(e) => setReferralSource(e.target.value)}
                  className="h-9 bg-white"
                />
              </div>
              <div className="flex-[2] min-w-[200px]">
                <Label htmlFor="quick-notes" className="text-xs">Notes</Label>
                <Input
                  id="quick-notes"
                  placeholder="Met at industry event, interested in tracking..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="h-9 bg-white"
                />
              </div>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

// ===== Warm Lead Card =====

interface WarmLeadCardProps {
  prospect: Prospect;
  onCall?: (prospectId: string, phone: string, name?: string) => void;
  onStatusChange?: (prospectId: string, status: ProspectStatus, extra?: Record<string, unknown>) => void;
  onFieldSave?: (prospectId: string, data: Record<string, string>) => Promise<void>;
  isSelected?: boolean;
  isUpdating?: boolean;
}

function WarmLeadCard({ prospect, onCall, onStatusChange, onFieldSave, isSelected, isUpdating }: WarmLeadCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCloseWon, setShowCloseWon] = useState(false);
  const [showCloseLost, setShowCloseLost] = useState(false);
  const [showDisqualify, setShowDisqualify] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(true);

  const displayName = prospect.name || prospect.company_name || 'Unknown';
  const status = prospect.status as ProspectStatus;
  const isClosedOrDisqualified = ['closed_won', 'closed_lost', 'disqualified', 'not_interested'].includes(status);
  const canQualify = status === 'new';
  const canMarkProposal = ['qualified', 'contacted', 'interested'].includes(status);
  const canClose = ['proposal', 'interested', 'contacted', 'qualified'].includes(status);
  const canRecordCallOutcome = !isClosedOrDisqualified && !['proposal', 'interested'].includes(status);
  const canMarkInterested = ['qualified', 'contacted', 'attempted'].includes(status);

  const hasValidPhone = prospect.phone &&
    prospect.phone !== 'pending' &&
    prospect.phone.trim() !== '' &&
    prospect.phone.toLowerCase() !== 'n/a';

  const arrivedTime = prospect.created_at ? formatRelativeTime(prospect.created_at) : '';

  const handleCopyPhone = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasValidPhone) {
      try {
        await navigator.clipboard.writeText(prospect.phone);
        toast.success('Phone number copied');
      } catch {
        toast.error('Failed to copy phone number');
      }
    }
  };

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasValidPhone && onCall) {
      onCall(prospect.id, prospect.phone, displayName);
      toast.info(`Preparing to call ${displayName}`);
    }
  };

  const handleQuickStatusChange = (newStatus: ProspectStatus) => {
    if (onStatusChange) {
      onStatusChange(prospect.id, newStatus);
    }
  };

  const handleCloseWon = (amount: number) => {
    if (onStatusChange) {
      onStatusChange(prospect.id, 'closed_won', {
        closed_amount: amount,
        closed_at: new Date().toISOString(),
      });
      setShowCloseWon(false);
    }
  };

  const handleCloseLost = (reason: string, notes?: string) => {
    if (onStatusChange) {
      onStatusChange(prospect.id, 'closed_lost', {
        lost_reason: reason,
        closed_at: new Date().toISOString(),
        ...(notes ? { qualification_notes: notes } : {}),
      });
      setShowCloseLost(false);
    }
  };

  const handleDisqualify = (notes: string) => {
    if (onStatusChange) {
      onStatusChange(prospect.id, 'disqualified', {
        qualification_notes: notes,
      });
      setShowDisqualify(false);
    }
  };

  return (
    <Card className={`group ${isSelected ? 'ring-2 ring-primary' : ''} ${isUpdating ? 'opacity-70' : ''}`}>
      {/* Compact Header - Always Visible */}
      <CardHeader
        className="pb-2 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold truncate">{displayName}</h3>
              <StatusBadge status={status} />
              {prospect.referral_source && (
                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                  via {prospect.referral_source}
                </Badge>
              )}
            </div>
            {prospect.company_name && prospect.name && (
              <p className="text-sm text-muted-foreground truncate">{prospect.company_name}</p>
            )}
            {prospect.email && (
              <p className="text-sm text-muted-foreground truncate">{prospect.email}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {hasValidPhone && !isClosedOrDisqualified && onCall && (
              <Button
                size="sm"
                onClick={handleCall}
                className={`h-8 px-3 transition-all duration-150 ${
                  isExpanded
                    ? 'bg-green-600 hover:bg-green-700 opacity-100'
                    : 'bg-green-600/70 hover:bg-green-700 opacity-40 hover:opacity-100'
                }`}
                disabled={isUpdating}
              >
                <Phone className="h-3 w-3 mr-1" />
                Call
              </Button>
            )}
            <ChevronDown
              className={`h-5 w-5 text-muted-foreground transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </div>
        </div>
      </CardHeader>

      {/* Expanded Content */}
      {isExpanded && (
        <CardContent className="pt-2 space-y-3">
          {/* Contact Info Section */}
          <CollapsibleSection
            title="Contact Info"
            icon={<User className="h-4 w-4 text-gray-500" />}
            defaultOpen
            open={contactOpen}
            onOpenChange={(open) => {
              if (!open && editingField?.startsWith('contact-')) setEditingField(null);
              setContactOpen(open);
            }}
            summary={[prospect.name, prospect.company_name, prospect.phone].filter(Boolean).join(' - ')}
            testId="contact-section"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <InlineEditField
                prospectId={prospect.id}
                fieldName="name"
                fieldKey="name"
                label="Name"
                currentValue={prospect.name || ''}
                inputType="text"
                onSave={onFieldSave!}
                icon={<User className="h-3 w-3" />}
                emptyText="Add name..."
                isEditing={editingField === 'contact-name'}
                onEditStart={() => setEditingField('contact-name')}
                onEditEnd={() => setEditingField(null)}
              />
              <InlineEditField
                prospectId={prospect.id}
                fieldName="company"
                fieldKey="company_name"
                label="Company"
                currentValue={prospect.company_name || ''}
                inputType="text"
                onSave={onFieldSave!}
                icon={<Building2 className="h-3 w-3" />}
                emptyText="Add company..."
                isEditing={editingField === 'contact-company'}
                onEditStart={() => setEditingField('contact-company')}
                onEditEnd={() => setEditingField(null)}
              />
              <InlineEditField
                prospectId={prospect.id}
                fieldName="phone"
                fieldKey="phone"
                label="Phone"
                currentValue={prospect.phone || ''}
                inputType="tel"
                onSave={onFieldSave!}
                icon={<Phone className="h-3 w-3" />}
                emptyText="Add phone..."
                isEditing={editingField === 'contact-phone'}
                onEditStart={() => setEditingField('contact-phone')}
                onEditEnd={() => setEditingField(null)}
                viewModeExtra={
                  hasValidPhone && editingField !== 'contact-phone' ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCopyPhone(e); }}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      title="Copy phone"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  ) : undefined
                }
              />
              <InlineEditField
                prospectId={prospect.id}
                fieldName="email"
                fieldKey="email"
                label="Email"
                currentValue={prospect.email || ''}
                inputType="email"
                onSave={onFieldSave!}
                icon={<Mail className="h-3 w-3" />}
                emptyText="Add email..."
                isEditing={editingField === 'contact-email'}
                onEditStart={() => setEditingField('contact-email')}
                onEditEnd={() => setEditingField(null)}
              />
            </div>
            {arrivedTime && (
              <p className="text-xs text-muted-foreground mt-2">Added {arrivedTime}</p>
            )}
          </CollapsibleSection>

          {/* Details Section */}
          <CollapsibleSection
            title="Details"
            icon={<FileText className="h-4 w-4 text-gray-500" />}
            defaultOpen
            open={detailsOpen}
            onOpenChange={(open) => {
              if (!open && editingField?.startsWith('details-')) setEditingField(null);
              setDetailsOpen(open);
            }}
            summary={prospect.notes ? prospect.notes.split('\n')[0].slice(0, 60) : undefined}
            testId="details-section"
          >
            <div className="space-y-2">
              <InlineEditField
                prospectId={prospect.id}
                fieldName="referral source"
                fieldKey="referral_source"
                label="Referral Source"
                currentValue={prospect.referral_source || ''}
                inputType="text"
                onSave={onFieldSave!}
                emptyText="Add referral source..."
                isEditing={editingField === 'details-referral'}
                onEditStart={() => setEditingField('details-referral')}
                onEditEnd={() => setEditingField(null)}
              />
              <InlineEditField
                prospectId={prospect.id}
                fieldName="notes"
                fieldKey="notes"
                label="Notes"
                currentValue={prospect.notes || ''}
                inputType="textarea"
                onSave={onFieldSave!}
                icon={<FileText className="h-3 w-3" />}
                emptyText="Add notes..."
                isEditing={editingField === 'details-notes'}
                onEditStart={() => setEditingField('details-notes')}
                onEditEnd={() => setEditingField(null)}
              />
              <InlineEditField
                prospectId={prospect.id}
                fieldName="qualification notes"
                fieldKey="qualification_notes"
                label="Qualification Notes"
                currentValue={prospect.qualification_notes || ''}
                inputType="textarea"
                onSave={onFieldSave!}
                emptyText="Add qualification notes..."
                isEditing={editingField === 'details-qualification'}
                onEditStart={() => setEditingField('details-qualification')}
                onEditEnd={() => setEditingField(null)}
              />
            </div>
          </CollapsibleSection>

          {/* Closed deal info */}
          {status === 'closed_won' && prospect.closed_amount && (
            <div className="flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 p-2 rounded-md border border-emerald-200">
              <DollarSign className="h-4 w-4 flex-shrink-0" />
              <span className="font-medium">Won: ${prospect.closed_amount.toLocaleString()}</span>
            </div>
          )}
          {status === 'closed_lost' && prospect.lost_reason && (
            <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 p-2 rounded-md border border-red-200">
              <ThumbsDown className="h-4 w-4 flex-shrink-0" />
              <span>Lost: {LOST_REASONS.find(r => r.value === prospect.lost_reason)?.label || prospect.lost_reason}</span>
            </div>
          )}

          {/* Workflow Actions */}
          {!isClosedOrDisqualified && (
            <div className="flex gap-2 flex-wrap border-t pt-3">
              {canRecordCallOutcome && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickStatusChange('attempted')}
                    disabled={isUpdating}
                    className="h-8 text-orange-700 border-orange-300 hover:bg-orange-50"
                  >
                    No Answer
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickStatusChange('contacted')}
                    disabled={isUpdating}
                    className="h-8 text-purple-700 border-purple-300 hover:bg-purple-50"
                  >
                    Callback
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickStatusChange('not_interested')}
                    disabled={isUpdating}
                    className="h-8 text-gray-600 border-gray-300 hover:bg-gray-50"
                  >
                    Not Interested
                  </Button>
                </>
              )}
              {canQualify && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickStatusChange('qualified')}
                  disabled={isUpdating}
                  className="h-8"
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Qualify
                </Button>
              )}
              {canMarkInterested && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickStatusChange('interested')}
                  disabled={isUpdating}
                  className="h-8 text-cyan-700 border-cyan-300 hover:bg-cyan-50"
                >
                  Interested
                </Button>
              )}
              {canMarkProposal && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickStatusChange('proposal')}
                  disabled={isUpdating}
                  className="h-8"
                >
                  Proposal Sent
                </Button>
              )}
              {canClose && (
                <>
                  <Button
                    size="sm"
                    onClick={() => setShowCloseWon(true)}
                    className="bg-green-600 hover:bg-green-700 h-8"
                    disabled={isUpdating}
                  >
                    <DollarSign className="h-3 w-3 mr-1" />
                    Won
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowCloseLost(true)}
                    disabled={isUpdating}
                    className="h-8"
                  >
                    Lost
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDisqualify(true)}
                disabled={isUpdating}
                className="text-muted-foreground h-8"
              >
                <X className="h-3 w-3 mr-1" />
                Disqualify
              </Button>
            </div>
          )}
        </CardContent>
      )}

      {/* Modals */}
      <CloseWonModal
        open={showCloseWon}
        onClose={() => setShowCloseWon(false)}
        onConfirm={handleCloseWon}
        prospectName={displayName}
        isLoading={isUpdating}
      />
      <CloseLostModal
        open={showCloseLost}
        onClose={() => setShowCloseLost(false)}
        onConfirm={handleCloseLost}
        prospectName={displayName}
        isLoading={isUpdating}
      />
      <DisqualifyModal
        open={showDisqualify}
        onClose={() => setShowDisqualify(false)}
        onConfirm={handleDisqualify}
        prospectName={displayName}
        isLoading={isUpdating}
      />
    </Card>
  );
}

// ===== Loading/Error/Empty States =====

function WarmListSkeleton() {
  return (
    <div className="space-y-4 max-w-4xl">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-32" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <Card className="p-8 text-center">
      <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
      <h3 className="text-lg font-medium mb-2">Unable to Load Leads</h3>
      <p className="text-sm text-muted-foreground mb-4">
        {error instanceof Error ? error.message : 'An unexpected error occurred.'}
      </p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      )}
    </Card>
  );
}

function EmptyState({ view }: { view: WarmView }) {
  const messages: Record<WarmView, { title: string; description: string }> = {
    new: {
      title: 'No New Warm Leads',
      description: 'Add a referral using the form above.',
    },
    follow_up: {
      title: 'No Follow-ups Needed',
      description: 'Leads needing follow-up will appear here.',
    },
    pipeline: {
      title: 'Pipeline Empty',
      description: 'Interested leads and proposals will appear here.',
    },
    closed: {
      title: 'No Closed Deals',
      description: 'Won and lost deals will appear here.',
    },
    all: {
      title: 'No Warm Leads Yet',
      description: 'Use the form above to add your first referral.',
    },
  };

  const { title, description } = messages[view];

  return (
    <Card className="p-8 text-center">
      <Flame className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
      <h3 className="text-lg font-medium mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </Card>
  );
}

// ===== Main Page =====

export function WarmLeadsPage() {
  const hasDialer = useHasDialer();
  const [currentView, setCurrentView] = useState<WarmView>('all');
  const [selectedProspect, setSelectedProspect] = useState<{
    id: string;
    phone: string;
    name?: string;
  } | null>(null);
  const [updatingProspectId, setUpdatingProspectId] = useState<string | null>(null);

  // Fetch all prospects and filter client-side by source
  const { data, isLoading, error, refetch } = useProspects({
    queue: 'all',
  });

  const updateProspectMutation = useUpdateProspect();

  // Filter for warm referral leads
  const warmLeads = useMemo(() => {
    return (data?.prospects || []).filter(
      (p) => p.source === 'warm_referral'
    );
  }, [data?.prospects]);

  // Calculate counts for each view
  const viewCounts = useMemo(() => {
    const counts: Record<WarmView, number> = {
      new: 0,
      follow_up: 0,
      pipeline: 0,
      closed: 0,
      all: warmLeads.length,
    };

    warmLeads.forEach((lead) => {
      const status = lead.status as ProspectStatus;
      if (VIEW_STATUSES.new?.includes(status)) counts.new++;
      if (VIEW_STATUSES.follow_up?.includes(status)) counts.follow_up++;
      if (VIEW_STATUSES.pipeline?.includes(status)) counts.pipeline++;
      if (VIEW_STATUSES.closed?.includes(status)) counts.closed++;
    });

    return counts;
  }, [warmLeads]);

  // Filter and sort leads based on current view
  const filteredLeads = useMemo(() => {
    let leads = [...warmLeads];

    if (currentView !== 'all') {
      const allowedStatuses = VIEW_STATUSES[currentView];
      if (allowedStatuses) {
        leads = leads.filter((lead) => allowedStatuses.includes(lead.status as ProspectStatus));
      }
    }

    // Sort by created_at (newest first)
    leads.sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return leads;
  }, [warmLeads, currentView]);

  const handleCallProspect = (prospectId: string, phone: string, name?: string) => {
    setSelectedProspect({ id: prospectId, phone, name });
  };

  const handleStatusChange = async (
    prospectId: string,
    status: ProspectStatus,
    extra?: Record<string, unknown>
  ) => {
    const prospect = warmLeads.find(p => p.id === prospectId);
    const prospectName = prospect?.name || prospect?.company_name || 'Lead';

    setUpdatingProspectId(prospectId);
    try {
      await updateProspectMutation.mutateAsync({
        id: prospectId,
        data: {
          status,
          ...extra,
        },
      });

      const statusLabel = STATUS_CONFIG[status]?.label || status;
      if (status === 'closed_won') {
        const amount = extra?.closed_amount as number;
        toast.success(`Deal closed! ${prospectName} - $${amount?.toLocaleString()}`);
      } else if (status === 'closed_lost') {
        toast.info(`Marked ${prospectName} as lost`);
      } else if (status === 'disqualified') {
        toast.info(`Disqualified ${prospectName}`);
      } else {
        toast.success(`Updated ${prospectName} to ${statusLabel}`);
      }
    } catch (err) {
      console.error('Failed to update prospect:', err);
      toast.error(`Failed to update ${prospectName}. Please try again.`);
    } finally {
      setUpdatingProspectId(null);
    }
  };

  const FRIENDLY_FIELD_NAMES: Record<string, string> = {
    name: 'name',
    company_name: 'company',
    phone: 'phone',
    email: 'email',
    referral_source: 'referral source',
    notes: 'notes',
    qualification_notes: 'qualification notes',
  };

  const handleFieldSave = useCallback(async (prospectId: string, data: Record<string, string>) => {
    const fieldKey = Object.keys(data)[0];
    const prospect = warmLeads.find(p => p.id === prospectId);
    const prospectName = prospect?.name || prospect?.company_name || 'Lead';
    const friendlyName = FRIENDLY_FIELD_NAMES[fieldKey] || fieldKey;

    await updateProspectMutation.mutateAsync({ id: prospectId, data });
    toast.success(`Updated ${friendlyName} for ${prospectName}`);
  }, [warmLeads, updateProspectMutation]);

  return (
    <div className={`h-full flex flex-col ${hasDialer ? 'lg:flex-row' : ''}`}>
      {/* Left side - Warm leads list */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <div className="border-b px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                <Flame className="h-6 w-6 text-orange-500" />
                Warm Leads
              </h1>
              <p className="text-sm text-muted-foreground">
                Referrals and warm introductions
              </p>
            </div>
            <Badge variant="secondary" className="whitespace-nowrap">
              {filteredLeads.length} {filteredLeads.length === 1 ? 'lead' : 'leads'}
            </Badge>
          </div>

          {/* Quick Entry Form */}
          <QuickEntryForm />

          {/* View Tabs */}
          <Tabs value={currentView} onValueChange={(v) => setCurrentView(v as WarmView)} className="mt-4">
            <TabsList className="w-full h-auto flex-wrap sm:grid sm:grid-cols-5 gap-1">
              <TabsTrigger value="new" className="text-xs flex-1 min-w-[60px]">
                New {viewCounts.new > 0 && `(${viewCounts.new})`}
              </TabsTrigger>
              <TabsTrigger value="follow_up" className="text-xs flex-1 min-w-[80px]">
                Follow-up {viewCounts.follow_up > 0 && `(${viewCounts.follow_up})`}
              </TabsTrigger>
              <TabsTrigger value="pipeline" className="text-xs flex-1 min-w-[70px]">
                Pipeline {viewCounts.pipeline > 0 && `(${viewCounts.pipeline})`}
              </TabsTrigger>
              <TabsTrigger value="closed" className="text-xs flex-1 min-w-[60px]">
                Closed {viewCounts.closed > 0 && `(${viewCounts.closed})`}
              </TabsTrigger>
              <TabsTrigger value="all" className="text-xs flex-1 min-w-[50px]">
                All {viewCounts.all > 0 && `(${viewCounts.all})`}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {isLoading ? (
            <WarmListSkeleton />
          ) : error ? (
            <ErrorState error={error} onRetry={() => refetch()} />
          ) : filteredLeads.length === 0 ? (
            <EmptyState view={currentView} />
          ) : (
            <div className="space-y-4 max-w-4xl">
              {filteredLeads.map((lead) => (
                <WarmLeadCard
                  key={lead.id}
                  prospect={lead}
                  onCall={hasDialer ? handleCallProspect : undefined}
                  onStatusChange={handleStatusChange}
                  onFieldSave={handleFieldSave}
                  isSelected={selectedProspect?.id === lead.id}
                  isUpdating={updatingProspectId === lead.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right side - Persistent dialer panel (hidden for CRM-only users) */}
      {hasDialer && (
        <div className="w-full lg:w-96 border-t lg:border-t-0 lg:border-l bg-muted/30 flex flex-col max-h-[50vh] lg:max-h-none">
          <div className="border-b px-4 sm:px-6 py-4">
            <h2 className="text-lg font-semibold">Dialer</h2>
            <p className="text-xs text-muted-foreground">
              {selectedProspect
                ? `Ready to call ${selectedProspect.name || 'lead'}`
                : 'Click "Call" on a lead to start'}
            </p>
          </div>

          <div className="flex-1 overflow-auto p-4 sm:p-6">
            <Card className="p-4">
              <CallUI
                prefilledPhone={selectedProspect?.phone}
                prospectId={selectedProspect?.id}
                prospectName={selectedProspect?.name}
              />
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
